import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../auth';
import type { Employee } from '../../types';
import { Card, Tag } from '../../components/ds';

interface AttendanceRecord {
  id: string;
  work_date: string;
  clock_in: string | null;
  clock_out: string | null;
  total_hours_worked: string;
  regular_hours: string;
  regular_ot_hours: string;
  rest_day_ot_hours: string;
  holiday_ot_hours: string;
  night_diff_hours: string;
  tardiness_minutes: number;
  undertime_minutes: number;
  status: string;
  employeeNo: string;
  firstName: string;
  lastName: string;
  shiftName: string | null;
}

interface Shift {
  id: string;
  code: string;
  name: string;
  start_time: string;
  end_time: string;
  grace_period_mins: number;
}

interface Holiday {
  id: string;
  name: string;
  holiday_date: string;
  holiday_type: string;
  branch_name: string | null;
}

export default function AttendanceDashboard() {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Employee>('/employees/me') });
  const [activeTab, setActiveTab] = useState<'dtr' | 'shifts' | 'holidays'>('dtr');

  const attendanceEnabled = me.data?.features?.feature_time_attendance ?? false;

  const todayStr = new Date().toISOString().slice(0, 10);
  const currentMonthStart = todayStr.slice(0, 8) + '01';

  // 1. Fetch DTR Records
  const recordsQuery = useQuery({
    queryKey: ['attendance-records', currentMonthStart, todayStr],
    queryFn: () =>
      api<AttendanceRecord[]>(
        `/attendance/records?startDate=${currentMonthStart}&endDate=${todayStr}`,
      ),
    enabled: attendanceEnabled,
  });

  // 2. Fetch Shifts
  const shiftsQuery = useQuery({
    queryKey: ['attendance-shifts'],
    queryFn: () => api<Shift[]>('/attendance/shifts'),
    enabled: attendanceEnabled,
  });

  // 3. Fetch Holidays
  const holidaysQuery = useQuery({
    queryKey: ['attendance-holidays'],
    queryFn: () => api<Holiday[]>(`/attendance/holidays?year=${new Date().getFullYear()}`),
    enabled: attendanceEnabled,
  });

  // Clock In / Out Mutation
  const clockMutation = useMutation({
    mutationFn: () =>
      api<{ action: string; record: AttendanceRecord }>('/attendance/clock', {
        method: 'POST',
        body: { workDate: todayStr },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance-records'] });
    },
  });

  if (me.isLoading) {
    return <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>Loading attendance workspace…</div>;
  }

  // Graceful degradation when Time & Attendance is not enabled
  if (!attendanceEnabled) {
    return (
      <div style={{ maxWidth: '800px', margin: '40px auto', padding: '0 20px' }}>
        <Card accent elevated style={{ padding: '36px', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            color: '#60a5fa',
            marginBottom: '20px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>

          <h2 style={{ fontSize: '22px', fontWeight: 600, margin: '0 0 8px 0', color: 'var(--color-fg)' }}>
            Time & Attendance Module Not Licensed
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--color-muted)', maxWidth: '520px', margin: '0 auto 24px auto', lineHeight: 1.5 }}>
            Daily time recording (DTR), overtime tracking, night shift differential calculation, and biometric sync are currently inactive for <strong>{me.data?.organization?.name || 'your company'}</strong>.
          </p>

          <div style={{
            display: 'inline-block',
            padding: '12px 20px',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '6px',
            fontSize: '13px',
            color: 'var(--color-muted)',
            textAlign: 'left',
          }}>
            <div style={{ fontWeight: 600, color: 'var(--color-fg)', marginBottom: '4px' }}>
              How to enable this module:
            </div>
            <div>1. Contact your platform account administrator.</div>
            <div>2. Activate the <strong>Time & Attendance License</strong> in the Superadmin Control Plane.</div>
          </div>
        </Card>
      </div>
    );
  }

  const records = recordsQuery.data || [];
  const todayRecord = records.find((r) => r.work_date === todayStr);
  const isClockedIn = Boolean(todayRecord?.clock_in && !todayRecord?.clock_out);

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '28px',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 600, margin: 0, letterSpacing: '-0.02em' }}>
              Time & Attendance Management
            </h1>
            <Tag tone="accent">DOLE Compliant</Tag>
          </div>
          <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: 'var(--color-muted)' }}>
            Daily time records (DTR), Philippine overtime premiums, night shift differentials, and holiday tracking.
          </p>
        </div>

        {/* Punch Clock Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            className={`btn ${isClockedIn ? 'btn-secondary' : 'btn-primary'}`}
            onClick={() => clockMutation.mutate()}
            disabled={clockMutation.isPending}
            style={{ minWidth: '150px' }}
          >
            {clockMutation.isPending
              ? 'Recording...'
              : isClockedIn
              ? 'Clock Out Now'
              : 'Web Clock In'}
          </button>
        </div>
      </div>

      {/* Quick Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        <Card>
          <div className="card-kicker">Today's Clock In</div>
          <div style={{ fontSize: '20px', fontWeight: 600, marginTop: '4px' }}>
            {todayRecord?.clock_in ? new Date(todayRecord.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Not clocked in'}
          </div>
          <div className="text-muted" style={{ fontSize: '11px' }}>Date: {todayStr}</div>
        </Card>

        <Card>
          <div className="card-kicker">Today's Clock Out</div>
          <div style={{ fontSize: '20px', fontWeight: 600, marginTop: '4px' }}>
            {todayRecord?.clock_out ? new Date(todayRecord.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : isClockedIn ? 'In Progress' : '—'}
          </div>
          <div className="text-muted" style={{ fontSize: '11px' }}>Status: {todayRecord ? todayRecord.status.toUpperCase() : 'PENDING'}</div>
        </Card>

        <Card>
          <div className="card-kicker">Regular Hours Worked</div>
          <div style={{ fontSize: '20px', fontWeight: 600, marginTop: '4px' }}>
            {todayRecord ? `${todayRecord.regular_hours} hrs` : '0.00 hrs'}
          </div>
          <div className="text-muted" style={{ fontSize: '11px' }}>Standard 8-hr cap</div>
        </Card>

        <Card>
          <div className="card-kicker">Overtime / Premium Hours</div>
          <div style={{ fontSize: '20px', fontWeight: 600, marginTop: '4px', color: '#38bdf8' }}>
            {todayRecord ? `${Number(todayRecord.regular_ot_hours) + Number(todayRecord.night_diff_hours)} hrs` : '0.00 hrs'}
          </div>
          <div className="text-muted" style={{ fontSize: '11px' }}>+25% OT / +10% NSD</div>
        </Card>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--color-border)', marginBottom: '24px' }}>
        <button
          onClick={() => setActiveTab('dtr')}
          style={{
            padding: '10px 16px',
            border: 'none',
            background: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: activeTab === 'dtr' ? '2px solid var(--color-accent)' : '2px solid transparent',
            color: activeTab === 'dtr' ? 'var(--color-accent)' : 'var(--color-muted)',
          }}
        >
          Daily Time Records (DTR)
        </button>
        <button
          onClick={() => setActiveTab('shifts')}
          style={{
            padding: '10px 16px',
            border: 'none',
            background: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: activeTab === 'shifts' ? '2px solid var(--color-accent)' : '2px solid transparent',
            color: activeTab === 'shifts' ? 'var(--color-accent)' : 'var(--color-muted)',
          }}
        >
          Work Shifts & Schedules
        </button>
        <button
          onClick={() => setActiveTab('holidays')}
          style={{
            padding: '10px 16px',
            border: 'none',
            background: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: activeTab === 'holidays' ? '2px solid var(--color-accent)' : '2px solid transparent',
            color: activeTab === 'holidays' ? 'var(--color-accent)' : 'var(--color-muted)',
          }}
        >
          Philippine Holiday Calendar
        </button>
      </div>

      {/* Tab 1: DTR Records */}
      {activeTab === 'dtr' && (
        <div style={{
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--color-border)', fontWeight: 600, fontSize: '14px' }}>
            Attendance & Overtime Ledger ({currentMonthStart} to {todayStr})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)' }}>
                  <th style={{ padding: '10px 16px' }}>Date</th>
                  <th style={{ padding: '10px 16px' }}>Employee</th>
                  <th style={{ padding: '10px 16px' }}>Clock In</th>
                  <th style={{ padding: '10px 16px' }}>Clock Out</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right' }}>Total Hrs</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right' }}>Regular OT</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right' }}>Night Diff</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right' }}>Tardy (mins)</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {records.length > 0 ? (
                  records.map((r) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600 }}>{r.work_date}</td>
                      <td style={{ padding: '12px 16px' }}>{r.lastName}, {r.firstName}</td>
                      <td style={{ padding: '12px 16px' }}>
                        {r.clock_in ? new Date(r.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {r.clock_out ? new Date(r.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>{r.total_hours_worked}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', color: Number(r.regular_ot_hours) > 0 ? '#38bdf8' : 'inherit' }}>
                        {r.regular_ot_hours}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>{r.night_diff_hours}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', color: r.tardiness_minutes > 0 ? '#f87171' : 'inherit' }}>
                        {r.tardiness_minutes}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <Tag tone={r.status === 'approved' ? 'accent' : 'neutral'}>
                          {r.status.toUpperCase()}
                        </Tag>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: 'var(--color-muted)' }}>
                      No attendance records found for this period. Click 'Web Clock In' above to log your first punch.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 2: Shifts */}
      {activeTab === 'shifts' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          {shiftsQuery.data?.map((s) => (
            <Card key={s.id} kicker={`Code: ${s.code}`} title={s.name}>
              <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--color-muted)' }}>
                Schedule: <strong>{s.start_time} - {s.end_time}</strong>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-muted)', marginTop: '4px' }}>
                Grace Period: {s.grace_period_mins} minutes
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Tab 3: Holidays */}
      {activeTab === 'holidays' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          {holidaysQuery.data?.map((h) => (
            <Card key={h.id} kicker={h.holiday_type.replace(/_/g, ' ').toUpperCase()} title={h.name}>
              <div style={{ marginTop: '8px', fontSize: '13px', fontWeight: 600 }}>
                Date: {h.holiday_date}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-muted)', marginTop: '4px' }}>
                Coverage: {h.branch_name || 'Nationwide (All Branches)'}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
